import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Pill } from '~/components/pill';
import { useBundleCodeLabel } from './bundle-labels';

/** Active / Inactive pill, with the translated reasons under it when inactive. */
export function BundleStatus({ active, reasons, compact }: { active?: boolean; reasons?: string[] | null; compact?: boolean }) {
	const { t } = useTranslation();
	const label = useBundleCodeLabel();
	const list = reasons ?? [];
	return (
		<div className="flex min-w-0 flex-col items-start gap-0.5">
			<Pill variant={active ? 'success' : 'warning'}>
				{active ? t('catalog.bundles.status.active') : t('catalog.bundles.status.inactive')}
			</Pill>
			{!active && list.length > 0 && (
				<span
					className={compact ? 'truncate text-[11px] text-foreground-muted' : 'text-xs text-foreground-muted'}
					title={list.map(label.reason).join(', ')}>
					{list.map(label.reason).join(', ')}
				</span>
			)}
		</div>
	);
}

/** Full-width banner for the detail page / product tab. */
export function BundleStatusBanner({ active, reasons }: { active?: boolean; reasons?: string[] | null }) {
	const { t } = useTranslation();
	const label = useBundleCodeLabel();
	const list = reasons ?? [];
	return active ? (
		<div className="flex items-start gap-2 rounded-md border border-success-strong/30 bg-success p-3 text-sm text-success-fg">
			<CheckCircle2 size={16} className="mt-0.5 shrink-0" />
			<span>{t('catalog.bundles.status.activeBanner')}</span>
		</div>
	) : (
		<div className="flex items-start gap-2 rounded-md border border-warning-strong/30 bg-warning p-3 text-sm text-warning-fg">
			<AlertTriangle size={16} className="mt-0.5 shrink-0" />
			<div className="flex flex-col gap-1">
				<span className="font-medium">{t('catalog.bundles.status.inactiveBanner')}</span>
				{list.length > 0 && (
					<ul className="list-disc pl-4 text-xs">
						{list.map(code => (
							<li key={code}>{label.reason(code)}</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
}
